import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { CURRENCIES } from '../../utils/currencies';

interface PayGrade {
  id: number;
  name: string;
  currency: string;
  minimum_amount: string | null;
  midpoint_amount: string | null;
  maximum_amount: string | null;
}

interface PayGradeForm {
  name: string;
  currency: string;
  minimum_amount: number | string;
  midpoint_amount: number | string;
  maximum_amount: number | string;
}

const emptyForm: PayGradeForm = { name: '', currency: 'VUV', minimum_amount: '', midpoint_amount: '', maximum_amount: '' };

export function PayGrades() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PayGrade[] | null>(null);
  const [editing, setEditing] = useState<'new' | PayGrade | null>(null);
  const [form, setForm] = useState<PayGradeForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<PayGrade[]>('/pay-grades').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PayGrade) => {
    setForm({
      name: item.name,
      currency: item.currency,
      minimum_amount: item.minimum_amount ?? '',
      midpoint_amount: item.midpoint_amount ?? '',
      maximum_amount: item.maximum_amount ?? '',
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof PayGradeForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/pay-grades', form);
      else if (editing) await api.put(`/pay-grades/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PayGrade) => {
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/pay-grades/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Pay Grades</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Currency</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="muted">{item.currency}</td>
                  <td className="right">
                    <IconButton icon={IconView} label="View" to={`/system/pay-grades/${item.id}`} />
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No pay grades yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add pay grade' : 'Edit pay grade'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Currency
              <select value={form.currency} onChange={set('currency')}>
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              {errors.currency && <small className="error">{errors.currency[0]}</small>}
            </label>
            <label>
              Minimum
              <input type="number" step="0.01" min="0" value={form.minimum_amount} onChange={set('minimum_amount')} />
              {errors.minimum_amount && <small className="error">{errors.minimum_amount[0]}</small>}
            </label>
            <label>
              Midpoint
              <input type="number" step="0.01" min="0" value={form.midpoint_amount} onChange={set('midpoint_amount')} />
              {errors.midpoint_amount && <small className="error">{errors.midpoint_amount[0]}</small>}
            </label>
            <label className="span-2">
              Maximum
              <input type="number" step="0.01" min="0" value={form.maximum_amount} onChange={set('maximum_amount')} />
              {errors.maximum_amount && <small className="error">{errors.maximum_amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
